import { useState } from 'react';
import { BUREAU_FOLLOW_TEMPLATES, type BureauFollowDTO } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useCancelFollow, useNudgeFollow, usePatchFollow, useToggleStep } from '../../api/bureau.js';

const SINCE = (iso: string) => {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600_000);
  if (h < 1) return 'à l’instant';
  if (h < 24) return `depuis ${h} h`;
  const d = Math.floor(h / 24);
  return `depuis ${d} j`;
};

export function FollowRow({ f, canManage, defaultOpen = false }: { f: BureauFollowDTO; canManage: boolean; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [waitDraft, setWaitDraft] = useState<string | null>(null);
  const toggle = useToggleStep();
  const nudge = useNudgeFollow();
  const patch = usePatchFollow();
  const cancel = useCancelFollow();
  const doneSteps = f.steps.filter((s) => s.done).length;
  const tpl = BUREAU_FOLLOW_TEMPLATES[f.type];
  const closed = f.status !== 'OPEN';

  const onStep = (stepId: string, done: boolean, managerOnly: boolean) => {
    if (managerOnly && !canManage) {
      toast.info('Cette étape est une validation : elle se coche côté pilotage.');
      return;
    }
    toggle.mutate({ followId: f.id, stepId, done }, {
      onError: () => toast.danger('L’étape n’a pas pu être enregistrée.'),
    });
  };

  return (
    <div className={`bx-follow lv-${f.level}`} data-testid={`bx-follow-${f.id}`}>
      <button type="button" className="bx-follow-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="bx-follow-main">
          <span className="bx-follow-tags">
            <span className={`bx-type t-${f.type.toLowerCase()}`}>{tpl.short}</span>
            {f.critical && !closed && <span className="bx-critical">À ne jamais oublier</span>}
          </span>
          <span className={closed ? 'bx-follow-title is-done' : 'bx-follow-title'}>{f.title}</span>
          <span className="bx-follow-due">{f.dueLabel}</span>
        </span>
        <span className="bx-follow-prog">
          <span className="bx-bar"><span style={{ width: `${Math.round((doneSteps / Math.max(f.steps.length, 1)) * 100)}%` }} /></span>
          <span className="bx-mono">{doneSteps}/{f.steps.length}</span>
        </span>
      </button>

      {f.waitingOn && !closed && (
        <div className="bx-follow-wait">
          <span>
            En attente de {f.waitingManager ? f.waitingOn : <b>{f.waitingOn}</b>}
            {f.waitingSince ? ` · ${SINCE(f.waitingSince)}` : ''}
            {f.lastNudgeAt ? ` · relancé ${SINCE(f.lastNudgeAt).replace('depuis ', 'il y a ')}` : ''}
          </span>
          <button
            type="button" className="bx-chip-btn warn"
            onClick={() => nudge.mutate(f.id, { onSuccess: () => toast.success(f.waitingManager ? `Relance envoyée à ${f.waitingOn}` : 'Relance notée') })}
          >
            Relancer
          </button>
        </div>
      )}

      {open && (
        <div className="bx-follow-body">
          <div className="bx-steps">
            {f.steps.map((s) => (
              <button
                type="button" key={s.id} className={s.done ? 'bx-step is-done' : 'bx-step'}
                onClick={() => onStep(s.id, !s.done, s.managerOnly)}
                disabled={closed && f.status === 'CANCELLED'}
              >
                <span className="bx-box" aria-hidden="true">{s.done ? '✓' : ''}</span>
                <span className="bx-step-label">
                  {s.label}
                  {s.managerOnly && <span className="bx-step-mgr">validation</span>}
                  {s.done && s.doneByName && <span className="bx-step-by"> · {s.doneByName.split(' ')[0]}</span>}
                </span>
              </button>
            ))}
          </div>
          {f.note && <div className="bx-note">{f.note}</div>}
          {!closed && (
            <div className="bx-follow-actions">
              {waitDraft === null ? (
                !f.waitingManager && (
                  <button type="button" className="btn btn-sm" onClick={() => setWaitDraft(f.waitingOn ?? '')}>
                    {f.waitingOn ? 'Modifier l’attente' : 'En attente de…'}
                  </button>
                )
              ) : (
                <form
                  className="bx-wait-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    patch.mutate({ id: f.id, waitingOn: waitDraft.trim() || null }, { onSuccess: () => setWaitDraft(null) });
                  }}
                >
                  <label className="sr-only" htmlFor={`wait-${f.id}`}>En attente de</label>
                  <input id={`wait-${f.id}`} className="input" value={waitDraft} placeholder="la compta, le fournisseur…" onChange={(e) => setWaitDraft(e.target.value)} autoFocus />
                  <button type="submit" className="btn btn-sm btn-primary">OK</button>
                  {f.waitingOn && <button type="button" className="btn btn-sm" onClick={() => patch.mutate({ id: f.id, waitingOn: null }, { onSuccess: () => setWaitDraft(null) })}>Plus d’attente</button>}
                  <button type="button" className="btn btn-sm" onClick={() => setWaitDraft(null)}>Annuler</button>
                </form>
              )}
              {canManage && (
                <button
                  type="button" className="btn btn-sm bx-ghost-danger"
                  onClick={() => { if (window.confirm(`Annuler le suivi « ${f.title} » ?`)) cancel.mutate(f.id); }}
                >
                  Annuler le suivi
                </button>
              )}
              <span className="bx-follow-meta">{f.createdByName ? `Créé par ${f.createdByName}` : ''}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
